// frontend/src/modules/invoicing/components/InvoiceActions.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { invoicesApi } from '../api';
import { downloadFile } from '../../payroll/api';
import type { Invoice } from '../types';
import { PayLinkButton } from './PayLinkButton';

/**
 * Auth is Bearer-header-only (no cookies), so a plain `<a href>` to the PDF
 * endpoint opens an unauthenticated tab that renders a JSON 401. Instead,
 * fetch the PDF as an authed blob and trigger a download via an object URL.
 */
function DownloadPdfButton({ invoice }: { invoice: Invoice }) {
  const [downloading, setDownloading] = React.useState(false);
  const docType = invoice.document_type ?? 'invoice';
  const prefix = docType === 'credit_note' ? 'credit-note' : docType === 'estimate' ? 'estimate' : 'invoice';
  const filename = `${prefix}-${invoice.invoice_number ?? invoice.id}.pdf`;

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadFile(invoicesApi.pdfUrl(invoice.id), filename);
    } catch (err: any) {
      toast.error('Download failed', { description: err.message ?? String(err) });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Button variant="outline" size="sm" disabled={downloading} onClick={handleDownload}>
      {downloading ? 'Downloading…' : 'Download PDF'}
    </Button>
  );
}

interface InvoiceActionsProps {
  invoice: Invoice;
  onChanged: (next: Invoice) => void;
  /** Opens the Record-payment dialog (owned by the detail page). */
  onRecordPayment?: () => void;
  /** Opens the Credit-note dialog (owned by the detail page). */
  onCreateCreditNote?: () => void;
}

/** Human labels for the document types that can be emailed. */
const DOC_NOUN: Record<string, string> = { invoice: 'invoice', estimate: 'estimate', credit_note: 'credit note' };

export function InvoiceActions({ invoice, onChanged, onRecordPayment, onCreateCreditNote }: InvoiceActionsProps) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [busy, setBusy] = React.useState<string | null>(null);

  async function run(label: string, fn: () => Promise<Invoice>) {
    setBusy(label);
    try {
      const next = await fn();
      onChanged(next);
      toast.success(`${label} succeeded`);
    } catch (err: any) {
      // The server refuses a send it cannot deliver rather than silently
      // reporting success. `client_missing_email` names the fix, so it gets a
      // headline of its own instead of a generic "Send failed".
      if (err?.code === 'client_missing_email') {
        toast.error('This client has no email address', { description: err.message ?? String(err) });
      } else {
        toast.error(`${label} failed`, { description: err.message ?? String(err) });
      }
    } finally {
      setBusy(null);
    }
  }

  const docType = invoice.document_type ?? 'invoice';
  const noun = DOC_NOUN[docType] ?? 'invoice';
  const isDraft = invoice.status === 'draft';
  const isPayable = invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'partial';
  const canCancel = invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue';

  /**
   * Re-deliver a document that is already out with the client: same number,
   * same amounts, no state change. It puts a real email in someone's inbox,
   * so it asks first.
   */
  async function handleResend() {
    const ok = await confirm({
      title: `Resend ${noun} ${invoice.invoice_number}?`,
      description: invoice.sent_at
        ? `This emails the same ${noun} to the client again. Nothing about the ${noun} changes — same number, same amounts, same status.`
        : `This ${noun} was never delivered. Emailing it now sends the same document — same number, same amounts — to the client.`,
      confirmLabel: 'Resend',
    });
    if (!ok) return;
    await run('Resend', () => invoicesApi.send(invoice.id));
  }

  // ── Credit notes are read-only documents ──
  if (docType === 'credit_note') {
    return (
      <div className="flex items-center gap-2">
        <DownloadPdfButton invoice={invoice} />
      </div>
    );
  }

  // ── Estimates: send / accept / decline / convert ──
  if (docType === 'estimate') {
    const isSentEstimate = invoice.status === 'sent';
    const canConvert = invoice.status === 'sent' || invoice.status === 'accepted';
    return (
      <div className="flex items-center gap-2">
        <DownloadPdfButton invoice={invoice} />
        {isDraft && (
          <Button size="sm" disabled={busy !== null}
            onClick={() => run('Send', () => invoicesApi.send(invoice.id))}>
            {busy === 'Send' ? 'Sending…' : 'Send'}
          </Button>
        )}
        {isSentEstimate && (
          <Button size="sm" variant={invoice.sent_at ? 'outline' : 'default'} disabled={busy !== null}
            onClick={handleResend}>
            {busy === 'Resend' ? 'Resending…' : 'Resend'}
          </Button>
        )}
        {isSentEstimate && (
          <Button size="sm" variant="outline" disabled={busy !== null}
            onClick={() => run('Mark accepted', () => invoicesApi.accept(invoice.id))}>
            {busy === 'Mark accepted' ? 'Marking…' : 'Mark accepted'}
          </Button>
        )}
        {isSentEstimate && (
          <Button size="sm" variant="ghost" disabled={busy !== null}
            onClick={() => run('Mark declined', () => invoicesApi.decline(invoice.id))}>
            {busy === 'Mark declined' ? 'Marking…' : 'Mark declined'}
          </Button>
        )}
        {canConvert && (
          <Button size="sm" disabled={busy !== null}
            onClick={async () => {
              setBusy('Convert');
              try {
                const inv = await invoicesApi.convert(invoice.id);
                toast.success(`Converted to invoice ${inv.invoice_number}`);
                navigate(`/dashboard/invoices/${inv.id}`);
              } catch (err: any) {
                toast.error('Convert failed', { description: err.message ?? String(err) });
              } finally { setBusy(null); }
            }}>
            {busy === 'Convert' ? 'Converting…' : 'Convert to invoice'}
          </Button>
        )}
        {isDraft && (
          <Button size="sm" variant="ghost" disabled={busy !== null}
            onClick={() => run('Cancel', () => invoicesApi.cancel(invoice.id))}>
            {busy === 'Cancel' ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    );
  }

  // ── Plain invoices ──
  return (
    <div className="flex items-center gap-2">
      <DownloadPdfButton invoice={invoice} />
      {isDraft && (
        <Button size="sm" disabled={busy !== null}
          onClick={() => run('Send', () => invoicesApi.send(invoice.id))}>
          {busy === 'Send' ? 'Sending…' : 'Send'}
        </Button>
      )}
      {isPayable && (
        <Button size="sm" variant={invoice.sent_at ? 'outline' : 'default'} disabled={busy !== null}
          onClick={handleResend}>
          {busy === 'Resend' ? 'Resending…' : 'Resend'}
        </Button>
      )}
      {isPayable && onRecordPayment && (
        <Button size="sm" disabled={busy !== null} onClick={onRecordPayment}>
          Record payment
        </Button>
      )}
      {isPayable && (
        <Button size="sm" variant="outline" disabled={busy !== null}
          onClick={() => run('Mark paid', () => invoicesApi.markPaid(invoice.id))}>
          {busy === 'Mark paid' ? 'Marking…' : 'Mark paid'}
        </Button>
      )}
      {(invoice.status === 'paid' || invoice.status === 'partial') && onCreateCreditNote && (
        <Button size="sm" variant="outline" disabled={busy !== null} onClick={onCreateCreditNote}>
          Credit note
        </Button>
      )}
      {isPayable && (
        <PayLinkButton invoiceId={invoice.id} disabled={busy !== null} />
      )}
      {canCancel && (
        <Button size="sm" variant="ghost" disabled={busy !== null}
          onClick={() => run('Cancel', () => invoicesApi.cancel(invoice.id))}>
          {busy === 'Cancel' ? 'Cancelling…' : 'Cancel'}
        </Button>
      )}
    </div>
  );
}
